/**
 * DeviceRow.jsx - Fleet Premium
 *
 * 2ECOMP: PremiumUI Refresh — DeviceRow layout GPSWOX
 *
 * Layout: [☐] [🚗16px] [Nome + Endereço/Timestamp — há Xmin] [🔥] [55km/h] [🔋] [⚠] [●] [⋮]
 * Altura fixa: DEVICE_ROW_HEIGHT (56px)
 *
 * Performance (2ECOMP: performance-fix):
 * - React.memo + arePropsEqual granular (10 campos position)
 * - position passada como prop do DeviceList (não useSelector aqui)
 * - useRelativeTime: singleton 60s, setState apenas se label mudou
 * - useAttributePreference: strictEqual evita re-renders
 * - followPopupDeviceIds: selector boolean por device (não array)
 * - Callbacks estáveis via useCallback no DeviceList (refs)
 * - Re-render por position update: apenas o row afetado
 * - Re-render por tick 60s: apenas rows parados > 5min com label diferente
 */

import {
  memo, useCallback, useMemo, useState, useTransition,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import {
  Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Divider, Checkbox, Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NavigationIcon from '@mui/icons-material/Navigation';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HistoryIcon from '@mui/icons-material/History';
import TerminalIcon from '@mui/icons-material/Terminal';
import EditIcon from '@mui/icons-material/Edit';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ErrorIcon from '@mui/icons-material/Error';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import Battery60Icon from '@mui/icons-material/Battery60';
import BatteryCharging60Icon from '@mui/icons-material/BatteryCharging60';
import Battery20Icon from '@mui/icons-material/Battery20';
import BatteryCharging20Icon from '@mui/icons-material/BatteryCharging20';
import { devicesActions } from '../store';
import { useTranslation } from '../common/components/LocalizationProvider';
import { getEffectiveStatus } from '../common/util/deviceColors';
import { deviceHasCamera } from '../common/util/cameras';
import {
  formatAlarm, formatBoolean, formatPercentage, formatSpeed, formatTime,
} from '../common/util/formatter';
import { useAttributePreference } from '../common/util/preferences';
import CommandModal from './components/CommandModal';
import EngineIcon from '../resources/images/data/engine.svg?react';
// 2ECOMP: DeviceRow P1 — ícones de categoria (top 5 apenas)
import CarIcon from '../resources/images/icon/car.svg?react';
import TruckIcon from '../resources/images/icon/truck.svg?react';
import MotorcycleIcon from '../resources/images/icon/motorcycle.svg?react';
import VanIcon from '../resources/images/icon/van.svg?react';
import DefaultVehicleIcon from '../resources/images/icon/default.svg?react';
import useRelativeTime from '../common/hooks/useRelativeTime';

import {
  DEVICE_ROW_HEIGHT,
  SELECTION_TRANSITION,
} from '../common/theme/layout';

// ============================================
// CONSTANTES - Cores estáticas (evita recálculo)
// ============================================
// 2ECOMP: PremiumUI Refresh — DeviceRow
const STATUS_COLORS = {
  moving: '#3b82f6',
  online: '#22c55e',
  stopped: '#22c55e',
  offline: '#ef4444',
  unknown: '#6b7280',
  idle: '#f59e0b',
  alarm: '#ef4444',
};

// ============================================
// HELPERS - Fora do componente (sem recriação)
// ============================================
const getStatusColor = (effectiveStatus, hasAlarm) => {
  if (hasAlarm) return STATUS_COLORS.alarm;
  return STATUS_COLORS[effectiveStatus] || STATUS_COLORS.idle;
};

// 2ECOMP: DeviceRow — bateria: cor por nível via theme palette
const getBatteryColor = (level) => (level > 70 ? 'success.main' : level > 30 ? 'warning.main' : 'error.main');

// 2ECOMP: DeviceRow P1 — mapa de ícones por categoria
const CATEGORY_ICONS = {
  car: CarIcon,
  truck: TruckIcon,
  semi: TruckIcon,
  motorcycle: MotorcycleIcon,
  moped: MotorcycleIcon,
  van: VanIcon,
  minibus: VanIcon,
  bus: VanIcon,
  pickup: TruckIcon,
};

// ============================================
// STYLES — 2ECOMP: PremiumUI Refresh — DeviceRow GPSWOX
// ============================================
const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    // 2ECOMP: PremiumUI Refresh — DeviceRow: container 56px
    row: {
      height: DEVICE_ROW_HEIGHT,
      minHeight: DEVICE_ROW_HEIGHT,
      maxHeight: DEVICE_ROW_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      padding: '0 8px 0 0',
      cursor: 'pointer',
      transition: SELECTION_TRANSITION,
      borderLeft: '3px solid transparent',
      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
      overflow: 'hidden',
      '&:hover': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
      },
    },
    rowSelected: {
      borderLeftColor: '#00d4aa',
      backgroundColor: 'rgba(0, 212, 170, 0.08)',
      '&:hover': {
        backgroundColor: 'rgba(0, 212, 170, 0.10)',
      },
    },

    // Status dot — à direita (antes do ⋮)
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      flexShrink: 0,
    },
    statusDotMoving: {
      animation: '$dotPulse 2s ease-in-out infinite',
    },
    '@keyframes dotPulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.5 },
    },

    // 2ECOMP: DeviceRow P1 — ícone categoria 16px
    categoryIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      opacity: 0.45,
      width: 20,
      marginRight: 4,
    },

    // 2ECOMP: PremiumUI Refresh — DeviceRow: coluna nome + timestamp
    nameColumn: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      overflow: 'hidden',
      justifyContent: 'center',
    },

    // 2ECOMP: PremiumUI Refresh — DeviceRow: nome 0.78rem, fontWeight 500
    deviceName: {
      fontSize: '0.78rem',
      fontWeight: 500,
      color: isDark ? 'rgba(255,255,255,0.90)' : 'rgba(0,0,0,0.85)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: 1.3,
    },
    deviceNameOffline: {
      opacity: 0.5,
    },

    // 2ECOMP: PremiumUI Refresh — DeviceRow: timestamp 0.65rem
    timestamp: {
      fontSize: '0.65rem',
      color: isDark ? 'rgba(255,255,255,0.40)' : 'rgba(0,0,0,0.40)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      lineHeight: 1.2,
    },

    // 2ECOMP: PremiumUI Refresh — DeviceRow: área direita (ícones + speed)
    rightArea: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      flexShrink: 0,
    },

    // 2ECOMP: PremiumUI Refresh — DeviceRow: velocidade texto
    speedText: {
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: '0.72rem',
      fontWeight: 500,
      minWidth: 52,
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },

    // Camera badge
    cameraBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      marginLeft: 4,
      color: '#3b82f6',
      fontSize: '0.6rem',
      fontWeight: 700,
      flexShrink: 0,
    },

    // Botão ⋮
    moreButton: {
      padding: 2,
      flexShrink: 0,
      marginLeft: 2,
      color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)',
      '&:hover': {
        color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      },
      '& svg': { fontSize: 16 },
    },

    // Menu icons
    menuIcon: {
      minWidth: '28px !important',
      '& svg': { fontSize: 16 },
    },
    followMenuIcon: {
      color: '#10b981',
      minWidth: '28px !important',
      '& svg': { fontSize: 16 },
    },
    followMenuIconActive: {
      color: '#ef4444',
      minWidth: '28px !important',
      '& svg': { fontSize: 16 },
    },
  };
});

// ============================================
// COMPARADOR OTIMIZADO — 2ECOMP: PremiumUI Refresh — DeviceRow
// Re-render SOMENTE quando dados relevantes mudam
// ============================================
const arePropsEqual = (prevProps, nextProps) => {
  if (prevProps.device !== nextProps.device) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.groups !== nextProps.groups) return false;
  if (prevProps.isHidden !== nextProps.isHidden) return false;
  if (prevProps.onToggleVisibility !== nextProps.onToggleVisibility) return false;

  const prevPos = prevProps.position;
  const nextPos = nextProps.position;
  if (!prevPos && !nextPos) return true;
  if (!prevPos || !nextPos) return false;
  // 2ECOMP: PremiumUI Refresh — DeviceRow estado rico: comparar motion state
  if ((prevPos.speed > 0) !== (nextPos.speed > 0)) return false;
  // 2ECOMP: DeviceRow P1 — comparar address
  if (prevPos.address !== nextPos.address) return false;
  return (
    prevPos.speed === nextPos.speed
    && prevPos.fixTime === nextPos.fixTime
    && prevPos.attributes?.motion === nextPos.attributes?.motion
    && prevPos.attributes?.alarm === nextPos.attributes?.alarm
    && prevPos.attributes?.ignition === nextPos.attributes?.ignition
    && prevPos.attributes?.batteryLevel === nextPos.attributes?.batteryLevel
    && prevPos.attributes?.charge === nextPos.attributes?.charge
  );
};

// ============================================
// COMPONENTE PRINCIPAL — 2ECOMP: PremiumUI Refresh — DeviceRow GPSWOX
// ============================================
const DeviceRow = memo(({ device, position, isSelected, groups = [], isHidden = false, onToggleVisibility }) => {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const t = useTranslation();
  const speedUnit = useAttributePreference('speedUnit', 'kn');

  // 2ECOMP: performance-fix v2 — useTransition para click não bloquear UI
  const [, startTransition] = useTransition();

  // Menu ⋮ state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [commandModalOpen, setCommandModalOpen] = useState(false);
  const [moveAnchor, setMoveAnchor] = useState(null);
  // 2ECOMP: performance-fix — selector boolean (não array) evita re-render de todos os rows
  const isFollowing = useSelector(
    (state) => (device ? state.devices.followPopupDeviceIds.includes(device.id) : false),
  );

  if (!device) return null;

  // Status calculation
  const effectiveStatus = getEffectiveStatus(
    device.status,
    position?.attributes?.motion,
    !!position,
  );
  const hasAlarm = !!position?.attributes?.alarm;
  const statusColor = getStatusColor(effectiveStatus, hasAlarm);
  const isOffline = effectiveStatus === 'offline' || effectiveStatus === 'unknown';
  const isMoving = effectiveStatus === 'moving';
  const hasCamera = deviceHasCamera(device, position);

  // 2ECOMP: PremiumUI Refresh — DeviceRow: ignition e battery
  const hasIgnition = position?.attributes?.ignition !== undefined;
  const ignitionOn = !!position?.attributes?.ignition;
  // 2ECOMP: DeviceRow — bateria: hasOwnProperty + charge (igual referência)
  const hasBattery = position?.attributes?.hasOwnProperty('batteryLevel') ?? false;
  const batteryLevel = position?.attributes?.batteryLevel;
  const batteryCharging = !!position?.attributes?.charge;

  // 2ECOMP: performance-fix — handler estável para checkbox
  const handleVisibilityChange = useCallback((e) => {
    e.stopPropagation();
    onToggleVisibility(device.id);
  }, [onToggleVisibility, device.id]);

  // 2ECOMP: performance-fix v2 — click com useTransition (não bloqueia scroll/UI)
  const handleClick = useCallback(() => {
    startTransition(() => {
      dispatch(devicesActions.selectId(device.id));
    });
  }, [dispatch, device.id, startTransition]);

  // Menu ⋮ handlers
  const handleMenuOpen = useCallback((e) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  }, []);

  const handleMenuClose = useCallback((e) => {
    if (e) e.stopPropagation();
    setMenuAnchor(null);
  }, []);

  const handleFollow = useCallback((e) => {
    e.stopPropagation();
    if (isFollowing) {
      dispatch(devicesActions.removeFollowPopup(device.id));
    } else {
      dispatch(devicesActions.addFollowPopup(device.id));
    }
    setMenuAnchor(null);
  }, [dispatch, device.id, isFollowing]);

  const handleEvents = useCallback((e) => {
    e.stopPropagation();
    setMenuAnchor(null);
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    navigate(`/eventmap?deviceId=${device.id}&from=${encodeURIComponent(startOfDay.toISOString())}&to=${encodeURIComponent(now.toISOString())}`);
  }, [navigate, device.id]);

  const handleHistory = useCallback((e) => {
    e.stopPropagation();
    setMenuAnchor(null);
    navigate(`/replay?deviceId=${device.id}`);
  }, [navigate, device.id]);

  const handleCommands = useCallback((e) => {
    e.stopPropagation();
    setMenuAnchor(null);
    setCommandModalOpen(true);
  }, []);

  const handleEdit = useCallback((e) => {
    e.stopPropagation();
    setMenuAnchor(null);
    navigate(`/settings/device/${device.id}`);
  }, [navigate, device.id]);

  const handleMoveOpen = useCallback((e) => {
    e.stopPropagation();
    setMoveAnchor(e.currentTarget);
  }, []);

  const handleMoveClose = useCallback(() => {
    setMoveAnchor(null);
  }, []);

  const handleMoveToGroup = useCallback(async (groupId) => {
    setMoveAnchor(null);
    setMenuAnchor(null);
    try {
      const response = await fetch(`/api/devices/${device.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...device, groupId: groupId || 0 }),
      });
      if (response.ok) {
        const updated = await response.json();
        dispatch(devicesActions.update([updated]));
      }
    } catch {
      // silently fail
    }
  }, [device, dispatch]);

  // 2ECOMP: DeviceRow P1 — endereço inline OU timestamp
  const secondLineText = useMemo(() => {
    if (position?.address) return position.address;
    if (position?.fixTime) return formatTime(position.fixTime, 'seconds');
    if (device.lastUpdate) return formatTime(device.lastUpdate, 'seconds');
    return t('deviceUnknown') || '\u2014';
  }, [position?.address, position?.fixTime, device.lastUpdate, t]);

  // 2ECOMP: DeviceRow P1 — tempo relativo (só parados, throttled 60s)
  const stoppedLabel = useRelativeTime(
    !isMoving && position?.fixTime ? position.fixTime : null,
    5,
  );

  // 2ECOMP: DeviceRow P1 — ícone categoria
  const CategoryIcon = CATEGORY_ICONS[device.category] || DefaultVehicleIcon;

  return (
    <Box
      className={cx(classes.row, isSelected && classes.rowSelected)}
      onClick={handleClick}
    >
      {/* 2ECOMP: PremiumUI Refresh — DeviceRow: checkbox visibilidade */}
      {onToggleVisibility && (
        <Checkbox
          size="small"
          checked={!isHidden}
          onChange={handleVisibilityChange}
          onClick={(e) => e.stopPropagation()}
          sx={{
            p: '2px',
            '& .MuiSvgIcon-root': { fontSize: 16 },
            color: 'text.disabled',
            '&.Mui-checked': { color: 'primary.main' },
            flexShrink: 0,
          }}
        />
      )}

      {/* 2ECOMP: DeviceRow P1 — ícone categoria */}
      <Box className={classes.categoryIcon}>
        <CategoryIcon width={16} height={16} />
      </Box>

      {/* 2ECOMP: PremiumUI Refresh — DeviceRow: nome + linha 2 (endereço/timestamp) */}
      <Box className={classes.nameColumn}>
        <Typography
          className={cx(classes.deviceName, isOffline && classes.deviceNameOffline)}
          component="span"
        >
          {device.name}
          {hasCamera && <span className={classes.cameraBadge}>CAM</span>}
        </Typography>
        <Typography className={classes.timestamp} component="span">
          {secondLineText}
          {stoppedLabel && (
            <span> — {stoppedLabel}</span>
          )}
        </Typography>
      </Box>

      {/* 2ECOMP: PremiumUI Refresh — DeviceRow: área direita (ignição + speed + battery + alarm) */}
      <Box className={classes.rightArea}>
        {/* Ignição */}
        {hasIgnition && (
          <Tooltip title={`${t('positionIgnition') || 'Ignition'}: ${formatBoolean(ignitionOn, t)}`}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'default' }}
              onClick={(e) => e.stopPropagation()}
            >
              <EngineIcon
                width={15}
                height={15}
                style={{
                  fill: ignitionOn ? '#4caf50' : '#9e9e9e',
                  color: ignitionOn ? '#4caf50' : '#9e9e9e',
                }}
              />
            </Box>
          </Tooltip>
        )}

        {/* Velocidade — só se online e tem position */}
        {device.status === 'online' && position && (
          <Typography
            className={classes.speedText}
            component="span"
            sx={{ color: position.speed > 0 ? 'text.primary' : 'text.disabled' }}
          >
            {formatSpeed(position.speed, speedUnit, t)}
          </Typography>
        )}

        {/* 2ECOMP: DeviceRow — bateria: 3 níveis + charge (igual referência) */}
        {hasBattery && (() => {
          const color = getBatteryColor(batteryLevel);
          const iconSx = { color, fontSize: 16 };
          let BatIcon;
          if (batteryLevel > 70) {
            BatIcon = batteryCharging ? BatteryChargingFullIcon : BatteryFullIcon;
          } else if (batteryLevel > 30) {
            BatIcon = batteryCharging ? BatteryCharging60Icon : Battery60Icon;
          } else {
            BatIcon = batteryCharging ? BatteryCharging20Icon : Battery20Icon;
          }
          return (
            <Tooltip title={`${t('positionBatteryLevel')}: ${formatPercentage(batteryLevel)}`}>
              <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => e.stopPropagation()}>
                <BatIcon sx={iconSx} />
              </IconButton>
            </Tooltip>
          );
        })()}

        {/* Alarm */}
        {hasAlarm && (
          <Tooltip title={`${t('eventAlarm') || 'Alarm'}: ${formatAlarm(position.attributes.alarm, t)}`}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'default' }}
              onClick={(e) => e.stopPropagation()}
            >
              <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
            </Box>
          </Tooltip>
        )}

        {/* 2ECOMP: PremiumUI Refresh — DeviceRow: status dot movido para direita */}
        <Box
          className={cx(classes.statusDot, isMoving && classes.statusDotMoving)}
          sx={{ backgroundColor: statusColor }}
        />

        {/* Menu ⋮ — PRESERVADO INTACTO */}
        <IconButton
          className={classes.moreButton}
          onClick={handleMenuOpen}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        <MenuItem onClick={handleFollow} dense>
          <ListItemIcon className={isFollowing ? classes.followMenuIconActive : classes.followMenuIcon}>
            <NavigationIcon />
          </ListItemIcon>
          <ListItemText>
            {isFollowing ? (t('deviceStopFollow') || 'Parar de seguir') : (t('deviceFollow') || 'Seguir')}
          </ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleEvents} dense>
          <ListItemIcon className={classes.menuIcon}>
            <EventNoteIcon />
          </ListItemIcon>
          <ListItemText>{t('reportEvents') || 'Eventos'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleHistory} dense>
          <ListItemIcon className={classes.menuIcon}>
            <HistoryIcon />
          </ListItemIcon>
          <ListItemText>{t('reportReplay') || 'Histórico'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCommands} dense>
          <ListItemIcon className={classes.menuIcon}>
            <TerminalIcon />
          </ListItemIcon>
          <ListItemText>{t('commandTitle') || 'Comando'}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEdit} dense>
          <ListItemIcon className={classes.menuIcon}>
            <EditIcon />
          </ListItemIcon>
          <ListItemText>{t('sharedEdit') || 'Editar'}</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        {/* 2ECOMP: PremiumUI Refresh — move to group */}
        <MenuItem onClick={handleMoveOpen} dense>
          <ListItemIcon className={classes.menuIcon}>
            <DriveFileMoveIcon />
          </ListItemIcon>
          <ListItemText>{t('groupMoveDevice') || 'Mover para grupo'}</ListItemText>
        </MenuItem>
      </Menu>
      {/* 2ECOMP: PremiumUI Refresh — submenu "move to group" */}
      <Menu
        anchorEl={moveAnchor}
        open={Boolean(moveAnchor)}
        onClose={handleMoveClose}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
      >
        <MenuItem
          onClick={() => handleMoveToGroup(0)}
          dense
          selected={!device.groupId}
        >
          <ListItemText>{t('groupNoGroup') || 'Sem grupo'}</ListItemText>
        </MenuItem>
        {groups.map((group) => (
          <MenuItem
            key={group.id}
            onClick={() => handleMoveToGroup(group.id)}
            dense
            selected={device.groupId === group.id}
          >
            <ListItemText>{group.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Command Modal */}
      <CommandModal
        open={commandModalOpen}
        onClose={() => setCommandModalOpen(false)}
        deviceId={device.id}
        deviceName={device.name}
        blocked={!!position?.attributes?.blocked}
        onSuccess={() => setCommandModalOpen(false)}
      />
    </Box>
  );
}, arePropsEqual);

DeviceRow.displayName = 'DeviceRow';

export default DeviceRow;
